// FloorPlanViewer.jsx
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Stack,
    Tooltip,
    Fab,
    CircularProgress,
    Modal,
    TextField,
    Typography,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    IconButton,
    Select,
    MenuItem,
    Snackbar,
    Alert,
    Checkbox,
    FormControlLabel,
    Backdrop,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import RoomIcon from '@mui/icons-material/Room';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useDeficiencies, ACTIONS, DeficiencyProvider } from '../Deficiencies/DeficiencyContext';
import DeficiencySidebar from '../../global/Components/DeficiencySidebar/DeficiencySidebar';
import { getStatusChipProps, DEFICIENCY_STATUSES } from '../Deficiencies/constants';
import PinSearch from './PinSearch';
import Breadcrumbs from '../../global/Components/Breadcrumbs';
import { findBestROIForPin } from '../../utils/roiUtils';
import { formatDate, getTodayFormatted } from '../../utils/dateUtils';
import { DEFAULT_PIN_COLOR, PIN_COLOR_OPTIONS, getPinColor } from '../../utils/pinColors';

// ROI Components and Hooks
import CompactRoomMenu from './components/ROIToolbar';
import ROIOverlay from './components/ROIOverlay';
import ROILabelingModal from './components/ROILabelingModal';
import { useROI } from './hooks/useROI';
import { useFloorplanGestures } from './hooks/useFloorplanGestures';
import RoomEditToolbar from './components/RoomEditToolbar';
import RoomListSidebar from './components/RoomListSidebar';

// Edit Mode Context
import { useEditMode } from '../../../contexts/EditModeContext';

// Image Annotator
import ImageAnnotatorModal from '../../global/Components/ImageAnnotator/ImageAnnotatorModal';
import {
    DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE,
    MAX_DEFICIENCY_IMAGE_BYTES,
} from '../../global/Components/ImageAnnotator/imageAnnotationUtils';

// Camera Capture Modal for mobile-first flow
import CameraCaptureModal from '../../global/Components/CameraCaptureModal';

// Services
import floorplanService from '../Siteplans/services/floorplanService';
import { fetchProjectById } from '../ProjectDashboard/services/projectDashboardService';

// Permissions
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import {
    isExternalSubcontractorRole,
    isExternalOwnerConsultantRole,
    canSeeDeferredCost,
} from '../../../utils/roles';

/** Base pin tuning plus a subtle zoom boost to offset perceived shrink while zooming in. */
const PIN_VISUAL_SCALE_MULTIPLIER = 1;
const PIN_ZOOM_GROWTH_EXPONENT = 0.1;
const PIN_MAX_ZOOM_BOOST = 1.37;
const PIN_MIN_ZOOM_BOOST = 1;
const MIN_SCALE_VALUE = 0.0001;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPinVisualScale = (currentScale, fitScale) => {
    const safeScale = Math.max(currentScale || 1, MIN_SCALE_VALUE);
    const safeFitScale = Math.max(fitScale || safeScale, MIN_SCALE_VALUE);
    const zoomRatio = Math.max(safeScale / safeFitScale, 1);
    const perceptualBoost = clamp(
        Math.pow(zoomRatio, PIN_ZOOM_GROWTH_EXPONENT),
        PIN_MIN_ZOOM_BOOST,
        PIN_MAX_ZOOM_BOOST
    );

    return PIN_VISUAL_SCALE_MULTIPLIER * perceptualBoost;
};

/* -------------------------------------------------------------------------- */
/*  Add Deficiency Modal Component                                            */
/* -------------------------------------------------------------------------- */
function AddDeficiencyModal({
    open,
    onClose,
    onSave,
    position,
    detectedROI,
    availableROIs,
    businesses,
    currentUser,
    prefillPhoto, // Photo from camera capture modal (consumed once on open)
}) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [deferredCost, setDeferredCost] = useState('');
    const [beforeImages, setBeforeImages] = useState([]);
    const beforeImagesRef = useRef([]);
    const [beforeImageError, setBeforeImageError] = useState(false);
    const [pinColor, setPinColor] = useState(DEFAULT_PIN_COLOR); // Default color
    const [selectedROI, setSelectedROI] = useState(null); // ROI selection state
    const prefillAppliedRef = useRef(false); // Track if prefill photo was applied
    const [annotatorOpen, setAnnotatorOpen] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const currentImageUrlRef = useRef(null);
    const [currentImageFile, setCurrentImageFile] = useState(null);
    const [internalOnly, setInternalOnly] = useState(false); // flag to set deficiency to internal only
    const [uploadError, setUploadError] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [assignedBusinessId, setAssignedBusinessId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Auto-populate ROI when detected ROI changes
    useEffect(() => {
        if (detectedROI) {
            setSelectedROI(detectedROI);
        } else {
            setSelectedROI(null);
        }
    }, [detectedROI]);

    // Reset form state when modal closes
    useEffect(() => {
        if (!open) {
            prefillAppliedRef.current = false;
            setTitle('');
            setDescription('');
            setDeferredCost('');
            setBeforeImages((prev) => {
                prev.forEach((image) => {
                    if (!image.isAnnotated && image.preview) {
                        URL.revokeObjectURL(image.preview);
                    }
                });
                return [];
            });
            setPinColor(DEFAULT_PIN_COLOR);
            setSelectedROI(null);
            setBeforeImageError(false);
            setInternalOnly(false);
            setAssignedBusinessId('');
            setIsSubmitting(false);
        }
    }, [open]);

    // Apply prefillPhoto only once when modal opens with it - route through annotation UI
    useEffect(() => {
        if (open && prefillPhoto && !prefillAppliedRef.current) {
            prefillAppliedRef.current = true;
            if (prefillPhoto.file?.size > MAX_DEFICIENCY_IMAGE_BYTES) {
                setUploadError(DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE);
                if (prefillPhoto.preview) URL.revokeObjectURL(prefillPhoto.preview);
                return;
            }

            // Open the annotation UI instead of directly adding to beforeImages
            // This matches the normal image upload flow
            setCurrentImageUrl(prefillPhoto.preview);
            setCurrentImageFile(prefillPhoto.file || null);
            setAnnotatorOpen(true);
        }
    }, [open, prefillPhoto]);

    const handleBeforeImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (beforeImages.length + files.length > 3) {
            setBeforeImageError(true);
            return;
        }

        setBeforeImageError(false);
        setUploadError(null);

        // Create object URL for the first selected file and open annotator
        if (files.length > 0) {
            setIsConverting(true);
            let file = files[0];
            if (file.size > MAX_DEFICIENCY_IMAGE_BYTES) {
                setUploadError(DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE);
                setIsConverting(false);
                event.target.value = '';
                return;
            }

            const lowerName = (file.name || '').toLowerCase();
            const lowerType = (file.type || '').toLowerCase();
            const isHeicOrHeif =
                lowerName.endsWith('.heic') ||
                lowerName.endsWith('.heif') ||
                lowerType.includes('heic') ||
                lowerType.includes('heif');

            if (isHeicOrHeif) {
                try {
                    const { default: heic2any } = await import('heic2any');
                    const jpegBlob = await heic2any({
                        blob: file,
                        toType: 'image/jpeg',
                        quality: 0.9,
                    });
                    file = new File(
                        [jpegBlob],
                        lowerName.replace(/\.(heic|heif)$/i, '.jpg') || 'image.jpg',
                        {
                            type: 'image/jpeg',
                        }
                    );
                } catch (e) {
                    console.warn('HEIC/HEIF conversion failed', e);
                    try {
                        setUploadError(
                            'This HEIC/HEIF variant could not be processed. Please export as JPEG/PNG.'
                        );
                        setIsConverting(false);
                        return;
                    } catch (e2) {
                        console.warn('WASM HEIC decode failed.', e2);
                        setUploadError(
                            'Could not process HEIC/HEIF image. Please resend as JPEG/PNG or try again.'
                        );
                        setIsConverting(false);
                        return;
                    }
                }
            }

            if (file.size > MAX_DEFICIENCY_IMAGE_BYTES) {
                setUploadError(DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE);
                setIsConverting(false);
                event.target.value = '';
                return;
            }

            const imageUrl = URL.createObjectURL(file);
            setCurrentImageUrl(imageUrl);
            setCurrentImageFile(file);
            setAnnotatorOpen(true);
            setIsConverting(false);
        }
    };

    const handleAnnotatorSave = (annotatedImageDataUrl, { hasAnnotations = true } = {}) => {
        // Keep untouched uploads as their original File. Only rasterize when the user actually
        // added an annotation.
        setBeforeImages((prev) => [
            ...prev,
            hasAnnotations
                ? {
                      preview: annotatedImageDataUrl,
                      isAnnotated: true,
                  }
                : {
                      preview: currentImageUrl,
                      isAnnotated: false,
                      file: currentImageFile,
                  },
        ]);

        // Annotated previews use their own data URL, so their source object URL can be released.
        // Untouched previews continue using the object URL until removal or form cleanup.
        if (hasAnnotations) URL.revokeObjectURL(currentImageUrl);
        setCurrentImageUrl(null);
        setCurrentImageFile(null);
        setAnnotatorOpen(false);
        setBeforeImageError(false);
    };

    const handleAnnotatorClose = () => {
        // Clean up the object URL
        if (currentImageUrl) {
            URL.revokeObjectURL(currentImageUrl);
            setCurrentImageUrl(null);
        }
        setCurrentImageFile(null);
        setAnnotatorOpen(false);
    };

    const handleRemoveBeforeImage = (index) => {
        setBeforeImages((prev) => {
            const newBeforeImages = [...prev];
            // Only revoke URL if it's not an annotated image (which uses data URLs)
            if (!newBeforeImages[index].isAnnotated && newBeforeImages[index].preview) {
                URL.revokeObjectURL(newBeforeImages[index].preview);
            }
            newBeforeImages.splice(index, 1);
            return newBeforeImages;
        });
        setBeforeImageError(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (beforeImages.length === 0) {
            setBeforeImageError(true);
            return;
        }
        setIsSubmitting(true);
        const selectedBusiness = businesses?.find((b) => b.businessId === assignedBusinessId);
        try {
            await onSave({
                title,
                description,
                deferredCost: deferredCost ? parseFloat(deferredCost) : null,
                beforeImages,
                pinColor,
                roiId: selectedROI?.id || null,
                roiName: selectedROI?.name || null,
                internalOnly,
                assignedToBusinessId: assignedBusinessId || null,
                assignedToBusinessName: selectedBusiness?.businessName || null,
                assignedOn: assignedBusinessId ? getTodayFormatted() : null,
                ...position,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModalClose = () => {
        if (isSubmitting) return;
        // Clear all form fields when modal is closed
        setTitle('');
        setDescription('');
        setDeferredCost('');
        // Cleanup image previews before clearing the array
        beforeImages.forEach((image) => {
            if (!image.isAnnotated && image.preview) {
                URL.revokeObjectURL(image.preview);
            }
        });
        setBeforeImages([]);
        setPinColor(DEFAULT_PIN_COLOR);
        setSelectedROI(null);
        setBeforeImageError(false);
        setInternalOnly(false);
        setAssignedBusinessId('');
        // Call the original onClose handler
        onClose();
    };

    // Keep unmount cleanup pointed at the latest state without revoking object URLs every time
    // an image moves from the annotator into the pending deficiency.
    useEffect(() => {
        beforeImagesRef.current = beforeImages;
    }, [beforeImages]);

    useEffect(() => {
        currentImageUrlRef.current = currentImageUrl;
    }, [currentImageUrl]);

    // Cleanup preview URLs when component unmounts
    useEffect(() => {
        return () => {
            beforeImagesRef.current.forEach((image) => {
                if (!image.isAnnotated && image.preview) {
                    URL.revokeObjectURL(image.preview);
                }
            });
            if (currentImageUrlRef.current) {
                URL.revokeObjectURL(currentImageUrlRef.current);
            }
        };
    }, []);

    return (
        <>
            <Modal
                open={open}
                onClose={handleModalClose}
                aria-labelledby="add-deficiency-modal"
                aria-describedby="modal-to-add-deficiency-details"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 400 },
                        maxHeight: { xs: '90vh', sm: '90vh' },
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: { xs: 2, sm: 4 },
                        borderRadius: 2,
                        overflowY: 'auto',
                    }}
                >
                    {isSubmitting && (
                        <Backdrop
                            open
                            sx={{
                                position: 'absolute',
                                zIndex: (theme) => theme.zIndex.modal + 1,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <CircularProgress size={40} sx={{ color: '#1652F0' }} />
                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                                Creating deficiency...
                            </Typography>
                        </Backdrop>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            id="add-deficiency-modal"
                            variant="h6"
                            component="h2"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            Add Deficiency
                        </Typography>

                        {/* Internal Only Checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={internalOnly}
                                    onChange={(e) => setInternalOnly(e.target.checked)}
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 20, // bigger checkbox
                                            borderRadius: 2, // round the corners a bit
                                        },
                                    }}
                                />
                            }
                            label="Internal Only"
                        />
                    </Box>

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 0 }}>
                        <TextField
                            fullWidth
                            required
                            id="title"
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            margin="normal"
                            size="small"
                        />

                        <TextField
                            fullWidth
                            id="description"
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            margin="normal"
                            multiline
                            rows={3}
                            size="small"
                        />

                        {/* ROI (Room) Selection */}
                        <FormControl fullWidth margin="normal" size="small">
                            <InputLabel id="roi-select-label">Room/Area</InputLabel>
                            <Select
                                labelId="roi-select-label"
                                id="roi-select"
                                value={selectedROI?.id || ''}
                                onChange={(e) => {
                                    const roiId = e.target.value;
                                    if (roiId === '') {
                                        setSelectedROI(null);
                                    } else {
                                        const roi = availableROIs.find((r) => r.id === roiId);
                                        setSelectedROI(roi ? { id: roi.id, name: roi.name } : null);
                                    }
                                }}
                                label="Room/Area"
                            >
                                <MenuItem value="">
                                    <em>No room assigned</em>
                                </MenuItem>
                                {availableROIs &&
                                    availableROIs.map((roi) => (
                                        <MenuItem key={roi.id} value={roi.id}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                {detectedROI?.id === roi.id && (
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: 'success.main',
                                                        }}
                                                    />
                                                )}
                                                {roi.name}
                                                {detectedROI?.id === roi.id && (
                                                    <Typography
                                                        variant="caption"
                                                        color="success.main"
                                                    >
                                                        (Auto-detected)
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        {/* Assignee Selection - Internal PMs and Supers only */}
                        {(currentUser?.role === 'INTERNAL_PM' ||
                            currentUser?.role === 'INTERNAL_SUPER' ||
                            currentUser?.role === 'INTERNAL_ADMIN') && (
                            <FormControl fullWidth margin="normal" size="small">
                                <InputLabel id="assignee-select-label">Assign to</InputLabel>
                                <Select
                                    labelId="assignee-select-label"
                                    id="assignee-select"
                                    value={assignedBusinessId}
                                    onChange={(e) => setAssignedBusinessId(e.target.value)}
                                    label="Assign to"
                                >
                                    <MenuItem value="">
                                        <em>No assignee</em>
                                    </MenuItem>
                                    {(businesses || [])
                                        .filter((b) => b && b.businessId)
                                        .map((b) => (
                                            <MenuItem key={b.businessId} value={b.businessId}>
                                                {b.businessName}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Deferred Cost and Pin Color on the same line.
                            Deferred Cost input is only shown to deferred-cost-permitted roles
                            (EXT-LAUNCH-009); other roles see only the Pin Color control. */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            {canSeeDeferredCost(currentUser?.role) && (
                                <FormControl fullWidth size="small">
                                    <InputLabel htmlFor="deferred-cost">Deferred Cost</InputLabel>
                                    <OutlinedInput
                                        id="deferred-cost"
                                        value={deferredCost}
                                        onChange={(e) => setDeferredCost(e.target.value)}
                                        startAdornment={
                                            <InputAdornment position="start">$</InputAdornment>
                                        }
                                        label="Deferred Cost"
                                        type="number"
                                    />
                                </FormControl>
                            )}

                            {/* Pin Color Selection */}
                            <FormControl sx={{ minWidth: 120 }} size="small">
                                <Select
                                    labelId="pin-color-label"
                                    id="pin-color-select"
                                    value={pinColor}
                                    onChange={(e) => setPinColor(e.target.value)}
                                >
                                    {PIN_COLOR_OPTIONS.map((color) => (
                                        <MenuItem key={color.value} value={color.value}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        bgcolor: color.color,
                                                        borderRadius: '50%',
                                                        width: 20,
                                                        height: 20,
                                                        marginRight: 1,
                                                    }}
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Photo Upload Section */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography
                                variant="h7"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <Box component="span" sx={{ color: 'error.main' }}>
                                    *
                                </Box>
                                Before Photos (Max 3)
                            </Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={
                                    isConverting ? <CircularProgress size={16} /> : <PhotoCamera />
                                }
                                disabled={beforeImages.length >= 3 || isConverting}
                                fullWidth
                                error={beforeImageError}
                                size="small"
                                sx={{
                                    mt: 1,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:focus-within': {
                                        outline: '2px solid #1652F0',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                {isConverting ? 'Converting…' : 'Upload & Annotate Image'}
                                <input
                                    type="file"
                                    aria-label="Add before photo"
                                    accept="image/*,.heic,.HEIC,.heif,.HEIF"
                                    onChange={handleBeforeImageUpload}
                                    disabled={beforeImages.length >= 3 || isConverting}
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        opacity: 0,
                                        cursor:
                                            beforeImages.length >= 3 || isConverting
                                                ? 'default'
                                                : 'pointer',
                                    }}
                                />
                            </Button>
                            {(beforeImageError || uploadError) && (
                                <Typography
                                    color="error"
                                    variant="caption"
                                    sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                                >
                                    {uploadError || 'Please upload at least one image'}
                                </Typography>
                            )}

                            {/* Image Previews */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mt: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start',
                                }}
                            >
                                {beforeImages.map((image, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            width: { xs: 80, sm: 100 },
                                            height: { xs: 80, sm: 100 },
                                        }}
                                    >
                                        <img
                                            src={image.preview}
                                            alt={`Preview ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 4,
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                bgcolor: 'background.paper',
                                                '&:hover': { bgcolor: 'background.paper' },
                                            }}
                                            onClick={() => handleRemoveBeforeImage(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                mt: 3,
                                display: 'flex',
                                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                gap: { xs: 1, sm: 2 },
                            }}
                        >
                            <Button onClick={handleModalClose} disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={isSubmitting}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Image Annotator Modal */}
            <ImageAnnotatorModal
                open={annotatorOpen}
                onClose={handleAnnotatorClose}
                imageUrl={currentImageUrl}
                onSave={handleAnnotatorSave}
            />
        </>
    );
}

AddDeficiencyModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    position: PropTypes.object,
    detectedROI: PropTypes.object,
    availableROIs: PropTypes.array,
    businesses: PropTypes.array,
    currentUser: PropTypes.object,
    prefillPhoto: PropTypes.object, // Photo from camera capture (consumed once)
};

/** -------------------------------------------------------------------------- */
/**  Converts a data URL to a File object                                        */
/** -------------------------------------------------------------------------- */
function dataURLToFile(dataUrl, filename) {
    const [meta, base64] = dataUrl.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
    }
    return new File([array], filename, { type: mime });
}

/* -------------------------------------------------------------------------- */
/*  Floor-plan viewer                                                         */
/* -------------------------------------------------------------------------- */
function FloorPlanViewer({ isSidebarOpen }) {
    const navigate = useNavigate();
    const { state: navState } = useLocation();
    const { selectedPlan, highlightDeficiencyId, fromSidebar } = navState || {};
    const { projectId, floorplanId } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // Detect touch-primary devices (phones/tablets) - doesn't trigger on touch-screen laptops
    const isTouchDevice = useMediaQuery('(pointer: coarse)');

    // Edit Mode state
    const { isEditMode, exitEditMode } = useEditMode();
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [mobileMoveMode, setMobileMoveMode] = useState(false);

    // Permissions
    const { canProject, isLoading: permissionsLoading, userId, getProjectRole } = usePermissions();
    const canWriteProject = canProject(projectId, PERMISSIONS.PROJECT_WRITE);
    const canWriteDeficiency = canProject(projectId, PERMISSIONS.DEFICIENCY_WRITE);

    // Per-pin drag permission: trades never, externals only own pins, internals any pin
    const role = getProjectRole(projectId);
    const isTrade = role === 'INTERNAL_TRADE' || role === 'EXTERNAL_SUBCONTRACTOR_TRADE';
    const isExternal = role?.startsWith('EXTERNAL_');
    const isExternalSubcontractor = isExternalSubcontractorRole(role);

    // external owners/consultants may only create
    // deficiencies while the project allows external submissions and the deadline has not passed.
    // The backend is authoritative; this mirrors it so the UI does not open the Add Deficiency
    // modal and can explain why submissions are unavailable.
    const isExternalCreator = isExternalOwnerConsultantRole(role);
    // null = not yet known (loading / not applicable); otherwise the server-derived decision
    // for the current project, tagged with its projectId to detect stale values on reuse.
    const [externalSubmissionState, setExternalSubmissionState] = useState(null);

    useEffect(() => {
        // Only external owners/consultants are subject to the gate; internal users and
        // subcontractors (who already cannot create) don't need the project settings.
        if (!isExternalCreator) return;
        let cancelled = false;
        // Clear any prior project's decision so a reused viewer never briefly applies a stale
        // (e.g. previously-open) value to a different/closed project.
        setExternalSubmissionState(null);
        fetchProjectById(projectId)
            .then((project) => {
                if (cancelled) return;
                // Trust the server-derived boolean (computed against server UTC time);
                // never re-derive the deadline boundary on the client.
                setExternalSubmissionState({
                    projectId,
                    allowed: project?.externalSubmissionsAllowed === true,
                    deadline: project?.externalSubmissionDeadline || null,
                });
            })
            .catch(() => {
                if (cancelled) return;
                // On failure, fail closed for external users so we never silently allow a
                // submission the backend will reject.
                setExternalSubmissionState({ projectId, allowed: false, deadline: null });
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, isExternalCreator]);

    // While we are still fetching the gate decision for an external creator, we don't yet
    // know whether submissions are open. Block creation (fail closed) but do NOT claim the
    // window is closed in the UI until the server confirms it.
    const externalSubmissionsLoading =
        isExternalCreator && externalSubmissionState?.projectId !== projectId;

    // True only once the server decision is loaded for this project AND it is closed.
    // Internal users are never affected.
    const externalSubmissionsClosed =
        isExternalCreator &&
        externalSubmissionState?.projectId === projectId &&
        externalSubmissionState.allowed === false;

    const externalSubmissionDeadline =
        isExternalCreator && externalSubmissionState?.projectId === projectId
            ? externalSubmissionState.deadline
            : null;
    const formattedExternalSubmissionDeadline = externalSubmissionDeadline
        ? formatDate(externalSubmissionDeadline)
        : null;

    const canCreateDeficiencyPins =
        canWriteDeficiency &&
        !isTrade &&
        !isExternalSubcontractor &&
        !externalSubmissionsClosed &&
        !externalSubmissionsLoading;

    const canDragPin = useCallback(
        (pin) => {
            if (!canWriteDeficiency) return false;
            if (isTrade) return false;
            if (isExternalSubcontractor) return false;
            if (isExternal) return pin.createdById === userId;
            return true;
        },
        [canWriteDeficiency, isTrade, isExternalSubcontractor, isExternal, userId]
    );

    // Whether this user can move any pins at all (used to show the Move Mode FAB)
    const canMovePins = canWriteDeficiency && !isTrade && !isExternalSubcontractor;

    // Lock the outer app scroll while the viewer is mounted so the page can't move
    // while the user pans/zooms the floor plan. Paired with `body.viewplan-locked`
    // rules in App.css that size MainContent to fill the viewport.
    useLayoutEffect(() => {
        document.body.classList.add('viewplan-locked');
        return () => document.body.classList.remove('viewplan-locked');
    }, []);

    // Define breadcrumb links
    const breadcrumbLinks = [
        { path: '/projects', label: 'Projects' },
        { path: `/projects/${projectId}`, label: 'Project Dashboard' },
        { path: `/projects/${projectId}/plans`, label: 'Drawings' },
        {
            path: `/projects/${projectId}/plans/${floorplanId}`,
            label: selectedPlan?.fileName || 'View Plan',
        },
    ];

    /* Guard: if user hard-refreshes the viewer route without floorplan ID -------- */
    useEffect(() => {
        if (!floorplanId) {
            navigate(-1);
        }
    }, [floorplanId, navigate]);

    const imgRef = useRef(null);
    const wrapperRef = useRef(null);
    const wasPanningRef = useRef(false);
    const panningStartTimeRef = useRef(null);
    // Track if a pin drag just completed to prevent accidental deficiency creation
    const wasDraggingPinRef = useRef(false);

    const [pins, setPins] = useState([]);
    const [filteredPins, setFilteredPins] = useState([]); // State for filtered pins
    const [fitScale, setFitScale] = useState(null); // ← computed scale
    const [currentScale, setCurrentScale] = useState(1);

    // State for deficiency modal
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingPin, setPendingPin] = useState(null);

    // State for camera-first mobile flow
    const [cameraModalOpen, setCameraModalOpen] = useState(false);
    const [prefillPhoto, setPrefillPhoto] = useState(null);

    // Add snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error',
    });

    // Highlighting state for pins
    const [highlightedPinId, setHighlightedPinId] = useState(null);

    // Floor plan list and navigation state for swipe navigation
    const [allFloorPlans, setAllFloorPlans] = useState([]);
    const [currentFloorPlanIndex, setCurrentFloorPlanIndex] = useState(-1);
    const [containerWidth, setContainerWidth] = useState(0);

    // ROI Hook - Initialize ROI functionality using floorplan ID from URL
    const {
        rois,
        currentPolygon,
        isDrawingMode,
        drawingMode,
        rectangleStart,
        isDeleteMode,
        isROIVisible,
        isLoading: roiLoading,
        error: roiError,
        showROIModal,
        selectedRoomId,
        roomSearchQuery,
        startRectangleDrawing,
        startPolygonDrawing,
        addPolygonPoint,
        completeROIDrawing,
        completeRectangle,
        cancelROIDrawing,
        undoLastPoint,
        saveROI,
        deleteROI,
        renameROI,
        toggleROIVisibility,
        setROIVisible,
        closeROIModal,
        getExistingROINames,
        clearError: clearROIError,
        clearDrawingState,
        setRectangleStart,
        selectRoom,
        clearRoomSelection,
        updateRoomSearch,
    } = useROI(floorplanId);

    // Get state, dispatch and computed values from context
    const {
        state: deficiencyState,
        dispatch,
        createDeficiency,
        updateDeficiency,
        loadFloorplanDeficiencies,
    } = useDeficiencies();

    const { selectedDeficiency } = deficiencyState;

    /* ---------------------------------------------------------------------- */
    /*  Handle page navigation from swipe gestures                            */
    /* ---------------------------------------------------------------------- */
    const handlePageChange = useCallback(
        (direction) => {
            // Validate we have floor plans loaded
            if (allFloorPlans.length === 0 || currentFloorPlanIndex === -1) {
                return;
            }

            // Calculate new index
            let newIndex;
            if (direction === 'next') {
                newIndex = currentFloorPlanIndex + 1;
            } else if (direction === 'prev') {
                newIndex = currentFloorPlanIndex - 1;
            } else {
                return; // Invalid direction
            }

            // Boundary checks
            if (newIndex < 0 || newIndex >= allFloorPlans.length) {
                // At first/last page, do nothing
                return;
            }

            // Get the target floor plan
            const targetFloorPlan = allFloorPlans[newIndex];

            // Navigate to new floor plan
            navigate(`/projects/${projectId}/plans/${targetFloorPlan.id}`, {
                state: {
                    selectedPlan: targetFloorPlan,
                },
                replace: false, // Add to history so user can go back
            });
        },
        [allFloorPlans, currentFloorPlanIndex, projectId, navigate]
    );

    // Swipe gesture hook for floor plan navigation
    const swipeGestureHandlers = useFloorplanGestures({
        currentScale,
        minScale: fitScale,
        onPageChange: handlePageChange,
        disabled: isEditMode || isDrawingMode || isDeleteMode, // Disable during edit/drawing modes
        containerWidth,
    });

    /* ---------------------------------------------------------------------- */
    /*  Set pins on mount                                                     */
    /* ---------------------------------------------------------------------- */
    useEffect(() => {
        if (!deficiencyState.deficiencies) return;

        setPins(deficiencyState.deficiencies);
        setFilteredPins(deficiencyState.deficiencies); // Initialize filtered pins
    }, [deficiencyState.deficiencies, setPins]);

    /* ---------------------------------------------------------------------- */
    /*  Show rooms by default when entering Edit Mode                         */
    /* ---------------------------------------------------------------------- */
    useEffect(() => {
        if (isEditMode) {
            setROIVisible(true);
        }
    }, [isEditMode, setROIVisible]);

    /* ---------------------------------------------------------------------- */
    /*  Handle pin highlighting from sidebar navigation                       */
    /* ---------------------------------------------------------------------- */
    useEffect(() => {
        if (fromSidebar && highlightDeficiencyId && deficiencyState.deficiencies) {
            // Find the pin to highlight
            const pinToHighlight = deficiencyState.deficiencies.find(
                (pin) => pin.deficiencyId === highlightDeficiencyId
            );

            if (pinToHighlight) {
                // Set the highlighted pin
                setHighlightedPinId(highlightDeficiencyId);

                // Clear highlight after animation duration
                setTimeout(() => {
                    setHighlightedPinId(null);
                }, 5000); // 5 seconds of highlighting
            }
        }
    }, [fromSidebar, highlightDeficiencyId, deficiencyState.deficiencies, dispatch]);

    /* ---------------------------------------------------------------------- */
    /*  Handle ESC key to cancel rectangle drawing                           */
    /* ---------------------------------------------------------------------- */
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && drawingMode === 'rectangle' && rectangleStart) {
                // Cancel rectangle drawing - clear first corner
                setRectangleStart(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [drawingMode, rectangleStart, setRectangleStart]);

    /* ---------------------------------------------------------------------- */
    /*  Load all floor plans for swipe navigation                            */
    /* ---------------------------------------------------------------------- */
    useEffect(() => {
        const loadFloorPlans = async () => {
            if (!projectId) return;

            try {
                const plans = await floorplanService.getFloorplansByProject(projectId);
                setAllFloorPlans(plans);

                // Find current floor plan index
                const currentIndex = plans.findIndex((plan) => plan.id === floorplanId);
                setCurrentFloorPlanIndex(currentIndex);
            } catch (error) {
                console.error('Error loading floor plans:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load floor plans for navigation',
                    severity: 'error',
                });
            }
        };

        loadFloorPlans();
    }, [projectId, floorplanId]);

    // Handle search results from PinSearch component
    const handleSearchResults = useCallback((results) => {
        setFilteredPins(results);
    }, []);

    // New function to handle automatic pin selection from search
    //TODO: determine if we want the sidebar to automatically open when a user searches and one pin
    //is returned.
    const handleSelectPin = useCallback(
        (pin) => {
            dispatch({
                type: ACTIONS.SET_SELECTED_DEFICIENCY,
                payload: pin,
            });
        },
        [dispatch]
    );

    /* ---------------------------------------------------------------------- */
    /*  Calculate "fit" scale whenever image loads OR wrapper resizes         */
    /* ---------------------------------------------------------------------- */
    useLayoutEffect(() => {
        if (!imgRef.current || !wrapperRef.current) return;

        const calcScale = () => {
            // Add safety checks to prevent null reference errors
            if (!imgRef.current || !wrapperRef.current) return;

            const natW = imgRef.current.naturalWidth; //  natural width of image
            const natH = imgRef.current.naturalHeight; //  natural height of image
            const rect = wrapperRef.current.getBoundingClientRect(); //  size of wrapper

            // Ensure we have valid dimensions
            if (natW === 0 || natH === 0 || rect.width === 0 || rect.height === 0) return;

            const newScale = Math.min(rect.width / natW, rect.height / natH) * 0.9; //  scale to fit wrapper
            setFitScale(parseFloat(newScale.toFixed(4))); // trim jitter
            setCurrentScale(parseFloat(newScale.toFixed(4)));

            // Also update container width for swipe gesture calculations
            setContainerWidth(rect.width);
        };

        /* On image load (may already be cached/complete) */
        if (imgRef.current.complete) calcScale();
        else imgRef.current.addEventListener('load', calcScale, { once: true });

        /* On wrapper resize */
        const ro = new ResizeObserver(calcScale);
        ro.observe(wrapperRef.current);

        return () => ro.disconnect();
    }, []);

    /* ---------------------------------------------------------------------- */
    /*  Convert click → % coordinates and open modal                          */
    /* ---------------------------------------------------------------------- */
    const handleImageClick = useCallback(
        (e) => {
            if (wasPanningRef.current) {
                return;
            }

            // Don't create new deficiency if we just finished dragging a pin
            if (wasDraggingPinRef.current) {
                return;
            }

            if (!imgRef.current) return;

            // Don't create deficiencies when in Edit Mode
            if (isEditMode) {
                return;
            }

            // If we're in ROI drawing mode, handle polygon point addition
            if (isDrawingMode) {
                // ROI click handling is managed by the ROIOverlay component
                return;
            }

            // If we're in delete mode, don't create pins
            if (isDeleteMode) {
                // ROI deletion is handled by the ROIOverlay component
                return;
            }

            // Gate: external owners/consultants whose submission window is closed get an
            // explanation rather than the normal Add Deficiency modal.
            if (externalSubmissionsClosed) {
                setSnackbar({
                    open: true,
                    message: formattedExternalSubmissionDeadline
                        ? `External submissions closed on ${formattedExternalSubmissionDeadline}. New deficiencies cannot be added.`
                        : 'External submissions are closed for this project. New deficiencies cannot be added.',
                    severity: 'info',
                });
                return;
            }

            // Gate: some roles can update assigned deficiencies but cannot create new ones.
            if (!canCreateDeficiencyPins) {
                return;
            }

            const rect = imgRef.current.getBoundingClientRect();
            const xPct = (e.clientX - rect.left) / rect.width;
            const yPct = (e.clientY - rect.top) / rect.height;

            // Create pending pin with position data
            const newPin = { id: crypto.randomUUID(), xPct, yPct };

            // Auto-detect ROI for this pin location
            const detectedROI = findBestROIForPin(newPin, rois);

            // Add ROI info to pending pin if detected
            const pinWithROI = {
                ...newPin,
                detectedROI: detectedROI
                    ? {
                          id: detectedROI.id,
                          name: detectedROI.name,
                      }
                    : null,
            };

            setPendingPin(pinWithROI);

            // Mobile-first flow: open camera modal on touch devices
            if (isTouchDevice) {
                setCameraModalOpen(true);
            } else {
                setModalOpen(true);
            }
        },
        [
            isEditMode,
            isDrawingMode,
            isDeleteMode,
            canCreateDeficiencyPins,
            externalSubmissionsClosed,
            formattedExternalSubmissionDeadline,
            rois,
            roiLoading,
            roiError,
            isTouchDevice,
        ]
    );

    /* ---------------------------------------------------------------------- */
    /*  Handle modal close                                                   */
    /* ---------------------------------------------------------------------- */
    const handleModalClose = () => {
        setModalOpen(false);
        setPendingPin(null);
        setPrefillPhoto(null); // Clear prefill photo from camera capture
    };

    /* ---------------------------------------------------------------------- */
    /*  Camera capture handlers (mobile-first flow)                          */
    /* ---------------------------------------------------------------------- */
    const handleCameraCapture = useCallback(
        (photoData) => {
            if (!canCreateDeficiencyPins) {
                setCameraModalOpen(false);
                setPendingPin(null);
                return;
            }
            setCameraModalOpen(false);
            setPrefillPhoto(photoData);
            setModalOpen(true);
        },
        [canCreateDeficiencyPins]
    );

    const handleCameraClose = useCallback(() => {
        if (!canCreateDeficiencyPins) {
            setPendingPin(null);
            return;
        }
        setCameraModalOpen(false);
        setModalOpen(true); // Open normal dialog without photo
    }, [canCreateDeficiencyPins]);

    /* ---------------------------------------------------------------------- */
    /*  Handle deficiency save                                               */
    /* ---------------------------------------------------------------------- */
    const handleDeficiencySave = async (deficiencyData) => {
        if (!canCreateDeficiencyPins) {
            setModalOpen(false);
            setCameraModalOpen(false);
            setPendingPin(null);
            setPrefillPhoto(null);
            return;
        }

        const deficiency = { ...deficiencyData };
        delete deficiency.id;

        // converts before images to correct format
        // Convert data URLs to files
        deficiency.beforeImages = deficiency.beforeImages.map((img, index) => {
            if (img.file instanceof File) {
                return img.file;
            }
            if (img.preview.startsWith('data:image')) {
                return dataURLToFile(img.preview, `before-${Date.now()}-${index}.jpg`);
            }
            return img; // fallback in case it's already a File or something else
        });

        // Set initial status — backend will override based on creator role,
        // but we send a reasonable default for immediate UI feedback.
        if (deficiency.assignedToBusinessId) {
            deficiency.status = DEFICIENCY_STATUSES.ASSIGNED.value;
        } else {
            deficiency.status = DEFICIENCY_STATUSES.SUBMITTED.value;
        }

        // Add required floorplan information
        deficiency.floorplanId = floorplanId;
        deficiency.floorplanName = selectedPlan?.fileName || 'Unknown Floor Plan';

        try {
            await createDeficiency(deficiency);

            // Show success message with ROI info
            if (deficiency.roiId && deficiency.roiName) {
                setSnackbar({
                    open: true,
                    message: `Deficiency created and assigned to room: ${deficiency.roiName}`,
                    severity: 'success',
                });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Deficiency created successfully',
                    severity: 'success',
                });
            }

            // Close modal
            setModalOpen(false);
            setPendingPin(null);
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Failed to create deficiency: ${error.message || 'Unknown error occurred'}`,
                severity: 'error',
            });
        }
    };

    /* ---------------------------------------------------------------------- */
    /*  Handle pin click                                                      */
    /* ---------------------------------------------------------------------- */
    const handlePinClick = (pin) => {
        const deficiency = pin;

        dispatch({
            type: ACTIONS.SET_SELECTED_DEFICIENCY,
            payload: deficiency,
        });
    };

    /* ---------------------------------------------------------------------- */
    /*  Handle pin drag end - update deficiency position                     */
    /* ---------------------------------------------------------------------- */
    const handlePinDragEnd = useCallback(
        async (pin, newPosition) => {
            // Set flag to prevent image click from triggering
            wasDraggingPinRef.current = true;

            // Clear the flag after a short delay to allow normal clicks
            setTimeout(() => {
                wasDraggingPinRef.current = false;
            }, 100);

            try {
                // Auto-detect new ROI for the updated position
                const detectedROI = findBestROIForPin(newPosition, rois);

                // Prepare update data
                const updateData = {
                    xPct: newPosition.xPct,
                    yPct: newPosition.yPct,
                    roiId: detectedROI?.id || null,
                    roiName: detectedROI?.name || null,
                };

                // Update deficiency via context
                await updateDeficiency(pin.deficiencyId, updateData);

                // Update local pins state to reflect the new position immediately
                setPins((currentPins) =>
                    currentPins.map((p) =>
                        p.deficiencyId === pin.deficiencyId
                            ? {
                                  ...p,
                                  ...newPosition,
                                  roiId: updateData.roiId,
                                  roiName: updateData.roiName,
                              }
                            : p
                    )
                );

                // Show success message
                if (detectedROI) {
                    setSnackbar({
                        open: true,
                        message: `Deficiency moved to room: ${detectedROI.name}`,
                        severity: 'success',
                    });
                } else {
                    setSnackbar({
                        open: true,
                        message: 'Deficiency position updated successfully',
                        severity: 'success',
                    });
                }
            } catch (error) {
                console.error('Error updating deficiency position:', error);
                setSnackbar({
                    open: true,
                    message: `Failed to update deficiency position: ${error.message || 'Unknown error'}`,
                    severity: 'error',
                });
            }
        },
        [rois, updateDeficiency, setPins]
    );

    /* ---------------------------------------------------------------------- */
    /*  Handle sidebar close                                                  */
    /* ---------------------------------------------------------------------- */
    const handleSidebarClose = useCallback(() => {
        dispatch({
            type: ACTIONS.CLEAR_SELECTED_DEFICIENCY,
        });
    }, [dispatch]);

    /* ---------------------------------------------------------------------- */
    /*  Wait for fitScale before mounting TransformWrapper                    */
    /* ---------------------------------------------------------------------- */
    if (fitScale === null) {
        return (
            <Box
                ref={wrapperRef}
                sx={{
                    height: '85vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
                {/* image rendered invisibly so onLoad can fire & calc fitScale */}
                <img ref={imgRef} src={selectedPlan?.imageUrl} alt="" style={{ display: 'none' }} />
            </Box>
        );
    }

    const handleCloseSnackbar = (_event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Handle ROI save with error handling
    const handleROISave = async (roiData) => {
        try {
            await saveROI(roiData);
            setSnackbar({
                open: true,
                message: `Room "${roiData.name}" created successfully!`,
                severity: 'success',
            });
            // Drawing mode stays active after saving, allowing quick addition of multiple rooms
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Failed to create room: ${error.message}`,
                severity: 'error',
            });
        }
    };

    // Handle exit from Edit Mode - clear all drawing state, hide rooms, and exit
    const handleExitEditMode = () => {
        clearDrawingState(); // Clear all ROI drawing state
        setROIVisible(false); // Hide rooms when exiting edit mode
        exitEditMode(); // Exit edit mode
    };

    // Handle room rename - reload deficiencies to get updated roiName from backend relationship
    const handleRoomRename = async (roiId, newName) => {
        try {
            await renameROI(roiId, newName);
            // Reload deficiencies to get updated roiName (derived from FloorplanRoi relationship)
            await loadFloorplanDeficiencies();
        } catch (error) {
            // Error already handled by renameROI (shows in ROI error snackbar)
        }
    };

    return (
        <>
            {isEditMode && (permissionsLoading || canWriteProject) ? (
                /* ============================================================
                   EDIT MODE LAYOUT (gated by PROJECT_WRITE)
                   ============================================================ */
                <Box
                    sx={{
                        position: 'fixed',
                        top: 64,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.default',
                        zIndex: 1000,
                    }}
                >
                    {/* Room Edit Toolbar - Fixed at top */}
                    <RoomEditToolbar
                        isDrawingMode={isDrawingMode}
                        drawingMode={drawingMode}
                        isROIVisible={isROIVisible}
                        currentPolygonLength={currentPolygon.length}
                        onStartRectangle={startRectangleDrawing}
                        onStartPolygon={startPolygonDrawing}
                        onToggleVisibility={toggleROIVisibility}
                        onUndo={undoLastPoint}
                        onComplete={completeROIDrawing}
                        onCancel={cancelROIDrawing}
                        onExitEditMode={handleExitEditMode}
                    />

                    {/* Grid2 Layout: Canvas + Room List Sidebar */}
                    <Box sx={{ flex: 1, overflow: 'hidden', width: '100%' }}>
                        <Grid container spacing={0} sx={{ height: '100%', width: '100%' }}>
                            {/* Canvas Column */}
                            <Grid
                                size={{ xs: 12, md: 8, lg: 9 }}
                                sx={{
                                    height: '100%',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    ref={wrapperRef}
                                    sx={{
                                        height: '100%',
                                        width: '100%',
                                        position: 'relative',
                                        bgcolor: 'background.default',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <TransformWrapper
                                        key={fitScale}
                                        initialScale={fitScale}
                                        minScale={fitScale}
                                        limitToBounds
                                        centerOnInit
                                        wheel={{
                                            wheelEnabled: true,
                                            touchPadEnabled: true,
                                            excluded: ['MuiFab-root'],
                                        }}
                                        doubleClick={{ disabled: true }}
                                        onTransformed={(ref, { scale: transformScale }) => {
                                            setCurrentScale(transformScale);
                                        }}
                                        onPanningStart={(ref, event) => {
                                            panningStartTimeRef.current = Date.now();
                                            // Swipe gesture handler (disabled in edit mode)
                                            swipeGestureHandlers.onPanningStart(ref, event);
                                        }}
                                        onPanning={(ref, event) => {
                                            // Swipe gesture handler (disabled in edit mode)
                                            swipeGestureHandlers.onPanning(ref, event);
                                        }}
                                        onPanningStop={(ref, event) => {
                                            const duration =
                                                Date.now() - (panningStartTimeRef.current ?? 0);
                                            if (duration > 150) {
                                                wasPanningRef.current = true;
                                            }
                                            setTimeout(() => {
                                                wasPanningRef.current = false;
                                            }, 0);
                                            // Swipe gesture handler (disabled in edit mode)
                                            swipeGestureHandlers.onPanningStop(ref, event);
                                        }}
                                    >
                                        {({ zoomIn, zoomOut, centerView, resetTransform }) => {
                                            const scale = currentScale;

                                            return (
                                                <>
                                                    {/* Zoom Controls */}
                                                    <Stack
                                                        sx={{
                                                            position: 'absolute',
                                                            top: { xs: 'auto', sm: 16 },
                                                            bottom: { xs: 20, sm: 'auto' },
                                                            left: { xs: '50%', sm: 16 },
                                                            transform: {
                                                                xs: 'translateX(-50%)',
                                                                sm: 'none',
                                                            },
                                                            flexDirection: {
                                                                xs: 'row',
                                                                sm: 'column',
                                                            },
                                                            alignItems: 'center',
                                                            gap: { xs: 2, sm: 1 },
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        <Tooltip title="Zoom In">
                                                            <Fab
                                                                size="small"
                                                                onClick={() => zoomIn()}
                                                            >
                                                                <AddIcon />
                                                            </Fab>
                                                        </Tooltip>
                                                        <Tooltip title="Zoom Out">
                                                            <Fab
                                                                size="small"
                                                                onClick={() => zoomOut()}
                                                            >
                                                                <RemoveIcon />
                                                            </Fab>
                                                        </Tooltip>
                                                        <Tooltip title="Reset View">
                                                            <Fab
                                                                size="small"
                                                                onClick={() => {
                                                                    resetTransform();
                                                                    setCurrentScale(fitScale);
                                                                    setTimeout(
                                                                        () => centerView(),
                                                                        500
                                                                    );
                                                                }}
                                                            >
                                                                <CenterFocusStrongIcon />
                                                            </Fab>
                                                        </Tooltip>
                                                    </Stack>

                                                    {/* Mobile: FAB to toggle room drawer */}
                                                    {isMobile && (
                                                        <Fab
                                                            color="primary"
                                                            size="medium"
                                                            onClick={() =>
                                                                setMobileDrawerOpen(
                                                                    !mobileDrawerOpen
                                                                )
                                                            }
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: 20,
                                                                right: 20,
                                                                zIndex: 10,
                                                            }}
                                                        >
                                                            <RoomIcon />
                                                        </Fab>
                                                    )}

                                                    {/* Canvas Image */}
                                                    <TransformComponent
                                                        wrapperStyle={{
                                                            width: '100%',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={handleImageClick}
                                                            sx={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                height: 'auto',
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                ref={imgRef}
                                                                src={selectedPlan?.imageUrl}
                                                                alt={selectedPlan?.fileName}
                                                                sx={{ display: 'block' }}
                                                            />

                                                            {/* ROI Overlay - Edit Mode */}
                                                            <ROIOverlay
                                                                scale={scale}
                                                                rois={rois}
                                                                currentPolygon={currentPolygon}
                                                                isDrawingMode={isDrawingMode}
                                                                drawingMode={drawingMode}
                                                                rectangleStart={rectangleStart}
                                                                isDeleteMode={isDeleteMode}
                                                                isROIVisible={isROIVisible}
                                                                selectedRoomId={selectedRoomId}
                                                                onPolygonPointAdd={addPolygonPoint}
                                                                onRectangleStart={setRectangleStart}
                                                                onRectangleComplete={
                                                                    completeRectangle
                                                                }
                                                                onROIDelete={deleteROI}
                                                                wasPanningRef={wasPanningRef}
                                                            />
                                                        </Box>
                                                    </TransformComponent>
                                                </>
                                            );
                                        }}
                                    </TransformWrapper>
                                </Box>
                            </Grid>

                            {/* Room List Sidebar Column - Hidden on mobile, shown on tablet/desktop */}
                            {!isMobile && (
                                <Grid size={{ md: 4, lg: 3 }} sx={{ height: '100%' }}>
                                    <RoomListSidebar
                                        variant="fixed"
                                        rooms={rois}
                                        selectedRoomId={selectedRoomId}
                                        searchQuery={roomSearchQuery}
                                        onSearchChange={updateRoomSearch}
                                        onSelectRoom={(roomId) => {
                                            if (selectedRoomId === roomId) {
                                                clearRoomSelection();
                                            } else {
                                                selectRoom(roomId);
                                            }
                                        }}
                                        onRenameRoom={handleRoomRename}
                                        onDeleteRoom={deleteROI}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    {/* Mobile: Room List Drawer */}
                    {isMobile && (
                        <RoomListSidebar
                            variant="drawer"
                            open={mobileDrawerOpen}
                            onToggle={() => setMobileDrawerOpen(!mobileDrawerOpen)}
                            rooms={rois}
                            selectedRoomId={selectedRoomId}
                            searchQuery={roomSearchQuery}
                            onSearchChange={updateRoomSearch}
                            onSelectRoom={(roomId) => {
                                if (selectedRoomId === roomId) {
                                    clearRoomSelection();
                                } else {
                                    selectRoom(roomId);
                                }
                            }}
                            onRenameRoom={handleRoomRename}
                            onDeleteRoom={deleteROI}
                        />
                    )}

                    {/* ROI Labeling Modal */}
                    <ROILabelingModal
                        open={showROIModal}
                        onClose={closeROIModal}
                        onSave={handleROISave}
                        polygon={currentPolygon}
                        existingNames={getExistingROINames()}
                        loading={roiLoading}
                    />

                    {/* Snackbars */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={5000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            sx={{ width: '100%' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>

                    {roiError && (
                        <Snackbar
                            open={Boolean(roiError)}
                            autoHideDuration={6000}
                            onClose={clearROIError}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <Alert onClose={clearROIError} severity="error" sx={{ width: '100%' }}>
                                {roiError}
                            </Alert>
                        </Snackbar>
                    )}
                </Box>
            ) : (
                /* ============================================================
                   NORMAL MODE LAYOUT (Original)
                   ============================================================ */
                <>
                    {/* Breadcrumbs and Search Bar */}
                    <Box sx={{ mb: 2 }}>
                        {/* Breadcrumbs row */}
                        <Box sx={{ mb: 2 }}>
                            <Breadcrumbs links={breadcrumbLinks} />
                        </Box>

                        {/* Search bar row - full width */}
                        <Box sx={{ width: '100%' }}>
                            <PinSearch
                                pins={pins}
                                onSearchResults={handleSearchResults}
                                onSelectPin={handleSelectPin}
                                currentUserRole={role}
                            />
                        </Box>

                        {/* External submissions closed banner (EXT-LAUNCH-003) */}
                        {externalSubmissionsClosed && (
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <Alert severity="info" sx={{ width: '100%' }}>
                                    {formattedExternalSubmissionDeadline
                                        ? `External submissions closed on ${formattedExternalSubmissionDeadline}. You can view existing deficiencies, but new ones cannot be added.`
                                        : 'External submissions are closed for this project. You can view existing deficiencies, but new ones cannot be added.'}
                                </Alert>
                            </Box>
                        )}
                    </Box>

                    <Box
                        ref={wrapperRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: '90vw',
                            '@media (min-width: 900px)': {
                                width: isSidebarOpen ? '68vw' : '90vw',
                            },

                            '@media (min-width: 1190px)': {
                                width: isSidebarOpen ? '77vw' : '90vw',
                            },
                            margin: '0 auto',
                            position: 'relative',
                            bgcolor: 'background.default',
                            overflow: 'hidden',
                        }}
                    >
                        {/*  key={fitScale} → remounts wrapper if the fitScale ever changes */}
                        <TransformWrapper
                            key={fitScale}
                            initialScale={fitScale}
                            minScale={fitScale}
                            limitToBounds
                            centerOnInit
                            //todo: debug below
                            //So this somehow is breaking our center on zoomout
                            //without it, it centers correctly but there is a delay
                            //centerZoomedOut
                            wheel={{
                                wheelEnabled: true,
                                touchPadEnabled: true,
                                excluded: ['MuiFab-root'],
                            }}
                            panning={{ disabled: mobileMoveMode }}
                            doubleClick={{ disabled: true }}
                            onTransformed={(ref, { scale: transformScale }) => {
                                setCurrentScale(transformScale);
                            }}
                            onPanningStart={(ref, event) => {
                                panningStartTimeRef.current = Date.now();
                                // Also call swipe gesture handler
                                swipeGestureHandlers.onPanningStart(ref, event);
                            }}
                            onPanning={(ref, event) => {
                                // Call swipe gesture handler to check for page navigation
                                swipeGestureHandlers.onPanning(ref, event);
                            }}
                            onPanningStop={(ref, event) => {
                                const duration = Date.now() - (panningStartTimeRef.current ?? 0);
                                // Consider it a real pan if it lasted > 150ms
                                // TODO: this might need to be fined tune. is a user clicks slowly then its considered a pan
                                if (duration > 150) {
                                    wasPanningRef.current = true;
                                }

                                // Clear the flag after the next tick to allow legit clicks
                                setTimeout(() => {
                                    wasPanningRef.current = false;
                                }, 0);

                                // Also call swipe gesture handler
                                swipeGestureHandlers.onPanningStop(ref, event);
                            }}
                        >
                            {({ zoomIn, zoomOut, centerView, resetTransform }) => {
                                const scale = currentScale;

                                return (
                                    <>
                                        {/* ─────────────  Toolbar  ───────────── */}
                                        <Stack
                                            sx={{
                                                position: 'absolute',
                                                top: { xs: 'auto', sm: 16 },
                                                bottom: { xs: 20, sm: 'auto' },
                                                left: { xs: '50%', sm: 16 },
                                                transform: { xs: 'translateX(-50%)', sm: 'none' },
                                                flexDirection: { xs: 'row', sm: 'column' },
                                                alignItems: 'center',
                                                gap: { xs: 2, sm: 1 },
                                                zIndex: 10,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex' }}>
                                                <Tooltip title="Zoom In" placement="top">
                                                    <Fab
                                                        size="small"
                                                        onClick={() => {
                                                            zoomIn();
                                                        }}
                                                        aria-label="zoom in"
                                                        sx={{
                                                            boxShadow: { xs: 2, sm: 1 },
                                                            '&:hover': {
                                                                boxShadow: { xs: 4, sm: 3 },
                                                            },
                                                        }}
                                                    >
                                                        <AddIcon />
                                                    </Fab>
                                                </Tooltip>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Tooltip title="Zoom Out" placement="top">
                                                    <Fab
                                                        size="small"
                                                        onClick={() => {
                                                            zoomOut();
                                                        }}
                                                        aria-label="zoom out"
                                                        sx={{
                                                            boxShadow: { xs: 2, sm: 1 },
                                                            '&:hover': {
                                                                boxShadow: { xs: 4, sm: 3 },
                                                            },
                                                        }}
                                                    >
                                                        <RemoveIcon />
                                                    </Fab>
                                                </Tooltip>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <Tooltip title="Reset View" placement="top">
                                                    <Fab
                                                        size="small"
                                                        onClick={() => {
                                                            resetTransform();
                                                            setCurrentScale(fitScale);
                                                            setTimeout(() => {
                                                                centerView(); // recenter based on image bounding box
                                                            }, 500);
                                                        }}
                                                        aria-label="reset view"
                                                        sx={{
                                                            boxShadow: { xs: 2, sm: 1 },
                                                            '&:hover': {
                                                                boxShadow: { xs: 4, sm: 3 },
                                                            },
                                                        }}
                                                    >
                                                        <CenterFocusStrongIcon />
                                                    </Fab>
                                                </Tooltip>
                                            </Box>
                                            <CompactRoomMenu
                                                isDrawingMode={isDrawingMode}
                                                isDeleteMode={isDeleteMode}
                                                disabled={roiLoading}
                                            />
                                            {/* Move Pins Mode — users who can reposition pins */}
                                            {canMovePins && (
                                                <Box sx={{ display: 'flex' }}>
                                                    <Tooltip
                                                        title={
                                                            mobileMoveMode
                                                                ? 'Exit Move Mode'
                                                                : 'Move Pins'
                                                        }
                                                        placement="top"
                                                    >
                                                        <Fab
                                                            size="small"
                                                            color={
                                                                mobileMoveMode
                                                                    ? 'primary'
                                                                    : 'default'
                                                            }
                                                            onClick={() =>
                                                                setMobileMoveMode((p) => !p)
                                                            }
                                                            aria-label={
                                                                mobileMoveMode
                                                                    ? 'exit move mode'
                                                                    : 'move pins'
                                                            }
                                                            sx={{
                                                                boxShadow: { xs: 2, sm: 1 },
                                                                '&:hover': {
                                                                    boxShadow: { xs: 4, sm: 3 },
                                                                },
                                                            }}
                                                        >
                                                            <OpenWithIcon />
                                                        </Fab>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Stack>

                                        {/* Move Mode Banner */}
                                        {mobileMoveMode && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    zIndex: 20,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: 'rgba(0, 0, 0, 0.72)',
                                                    backdropFilter: 'blur(8px)',
                                                    borderRadius: 6,
                                                    px: 1.75,
                                                    py: 0.75,
                                                    color: 'common.white',
                                                    whiteSpace: 'nowrap',
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                <OpenWithIcon
                                                    sx={{ fontSize: 15, opacity: 0.85 }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.72rem',
                                                        letterSpacing: 0.2,
                                                    }}
                                                >
                                                    Move Mode — drag pins to reposition
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => setMobileMoveMode(false)}
                                                    sx={{
                                                        ml: 0.5,
                                                        py: 0.25,
                                                        px: 1.25,
                                                        minWidth: 0,
                                                        bgcolor: 'primary.main',
                                                        color: 'white',
                                                        borderRadius: 4,
                                                        textTransform: 'none',
                                                        fontSize: '0.72rem',
                                                        fontWeight: 700,
                                                        lineHeight: 1.6,
                                                        '&:hover': { bgcolor: 'primary.dark' },
                                                    }}
                                                >
                                                    Done
                                                </Button>
                                            </Box>
                                        )}

                                        {/* ─────────────  Transform contents  ───────────── */}
                                        <TransformComponent
                                            wrapperStyle={{ width: '100%', height: '100%' }}
                                        >
                                            <Box
                                                onClick={handleImageClick}
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: 'auto',
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    ref={imgRef}
                                                    src={selectedPlan?.imageUrl}
                                                    alt={selectedPlan?.fileName}
                                                    sx={{ display: 'block' }}
                                                />

                                                {/* ROI Overlay - Must be above pins */}
                                                <ROIOverlay
                                                    scale={scale}
                                                    rois={rois}
                                                    currentPolygon={currentPolygon}
                                                    isDrawingMode={isDrawingMode}
                                                    drawingMode={drawingMode}
                                                    rectangleStart={rectangleStart}
                                                    isDeleteMode={isDeleteMode}
                                                    isROIVisible={isROIVisible}
                                                    selectedRoomId={selectedRoomId}
                                                    onPolygonPointAdd={addPolygonPoint}
                                                    onRectangleStart={setRectangleStart}
                                                    onRectangleComplete={completeRectangle}
                                                    onROIDelete={deleteROI}
                                                    wasPanningRef={wasPanningRef}
                                                />

                                                {/* Pins layer – inverse scaling - now using filteredPins */}
                                                {!isEditMode && (
                                                    <PinsOverlay
                                                        pins={filteredPins}
                                                        scale={scale}
                                                        fitScale={fitScale}
                                                        onPinClick={handlePinClick}
                                                        highlightedPinId={highlightedPinId}
                                                        canDragPin={canDragPin}
                                                        onPinDragEnd={handlePinDragEnd}
                                                        imgRef={imgRef}
                                                        mobileMoveMode={mobileMoveMode}
                                                    />
                                                )}
                                            </Box>
                                        </TransformComponent>
                                    </>
                                );
                            }}
                        </TransformWrapper>

                        {/* Camera Capture Modal - Mobile-first flow */}
                        <CameraCaptureModal
                            open={cameraModalOpen}
                            onClose={handleCameraClose}
                            onCapture={handleCameraCapture}
                        />

                        {/* Deficiency Modal */}
                        <AddDeficiencyModal
                            open={modalOpen}
                            onClose={handleModalClose}
                            onSave={handleDeficiencySave}
                            position={
                                pendingPin ? { xPct: pendingPin.xPct, yPct: pendingPin.yPct } : null
                            }
                            detectedROI={pendingPin?.detectedROI || null}
                            availableROIs={rois}
                            businesses={deficiencyState.businesses}
                            currentUser={deficiencyState.currentUser}
                            prefillPhoto={prefillPhoto}
                        />

                        {/* Global Deficiency Detail Sidebar */}
                        <DeficiencySidebar
                            open={Boolean(selectedDeficiency)}
                            deficiency={selectedDeficiency}
                            onClose={handleSidebarClose}
                            getStatusChipProps={getStatusChipProps}
                        />

                        {/* ROI Labeling Modal */}
                        <ROILabelingModal
                            open={showROIModal}
                            onClose={closeROIModal}
                            onSave={handleROISave}
                            polygon={currentPolygon}
                            existingNames={getExistingROINames()}
                            loading={roiLoading}
                        />

                        {/* Error Snackbar */}
                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={5000}
                            onClose={handleCloseSnackbar}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            <Alert
                                onClose={handleCloseSnackbar}
                                severity={snackbar.severity}
                                sx={{ width: '100%' }}
                            >
                                {snackbar.message}
                            </Alert>
                        </Snackbar>

                        {/* ROI Error Snackbar */}
                        {roiError && (
                            <Snackbar
                                open={Boolean(roiError)}
                                autoHideDuration={6000}
                                onClose={clearROIError}
                                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            >
                                <Alert
                                    onClose={clearROIError}
                                    severity="error"
                                    sx={{ width: '100%' }}
                                >
                                    ROI Error: {roiError}
                                </Alert>
                            </Snackbar>
                        )}
                    </Box>
                </>
            )}
        </>
    );
}

// Add PropTypes for FloorPlanViewer
FloorPlanViewer.propTypes = {
    isSidebarOpen: PropTypes.bool.isRequired,
};

/* -------------------------------------------------------------------------- */
/*  Pins overlay – renders deficiency pins with drag-to-move functionality    */
/*  Features:                                                                  */
/*  - Move Mode off: click/tap pin to open sidebar — drag reposition is OFF   */
/*  - Move Mode on (FAB): pins become draggable; panning disabled on canvas   */
/*  - Same Move Mode rule for mouse and touch                                   */
/*  - 5px movement threshold distinguishes clicks/taps from drags             */
/*  - Inverse scaling keeps pins constant size at all zoom levels             */
/*  - Non-passive touch listeners prevent pan/zoom interference               */
/* -------------------------------------------------------------------------- */
function PinsOverlay({
    pins,
    scale = 1,
    fitScale = 1,
    onPinClick,
    highlightedPinId,
    canDragPin,
    onPinDragEnd,
    imgRef,
    mobileMoveMode = false,
}) {
    const [draggingPin, setDraggingPin] = useState(null);
    const [dragPosition, setDragPosition] = useState(null);
    const isDraggingRef = useRef(false);
    const dragStartPos = useRef(null);
    const pinRefs = useRef({});
    const canDragRef = useRef(false);
    const pinVisualScale = getPinVisualScale(scale, fitScale);
    const inversePinScale = pinVisualScale / Math.max(scale, MIN_SCALE_VALUE);

    // Reset drag state when leaving move mode
    useEffect(() => {
        if (!mobileMoveMode) {
            setDraggingPin(null);
            setDragPosition(null);
            isDraggingRef.current = false;
            canDragRef.current = false;
            dragStartPos.current = null;
        }
    }, [mobileMoveMode]);

    const handleMouseDown = useCallback(
        (e, pin) => {
            // Only allow dragging with left mouse button
            if (e.button !== 0) return;

            e.stopPropagation();
            isDraggingRef.current = false;
            canDragRef.current = false;
            dragStartPos.current = { x: e.clientX, y: e.clientY };

            setDraggingPin(pin);
            setDragPosition({ xPct: pin.xPct, yPct: pin.yPct });

            // Drag only in Move Mode (matches touch). Click-to-open still uses handleDragEnd.
            if (mobileMoveMode && (!canDragPin || canDragPin(pin))) {
                canDragRef.current = true;
            }
        },
        [canDragPin, mobileMoveMode]
    );

    const handleTouchStart = useCallback(
        (e, pin) => {
            // Prevent touch from triggering pan/zoom
            e.preventDefault();
            e.stopPropagation();

            const touch = e.touches[0];
            isDraggingRef.current = false;
            canDragRef.current = false;
            dragStartPos.current = { x: touch.clientX, y: touch.clientY };

            setDraggingPin(pin);
            setDragPosition({ xPct: pin.xPct, yPct: pin.yPct });

            if (mobileMoveMode && (!canDragPin || canDragPin(pin))) {
                canDragRef.current = true;
            }
        },
        [canDragPin, mobileMoveMode]
    );

    const handleMouseMove = useCallback(
        (e) => {
            if (!draggingPin || !imgRef?.current) return;

            // Only start a drag after movement exceeds threshold (avoids accidental drags on click)
            if (!isDraggingRef.current && canDragRef.current && dragStartPos.current) {
                const dx = e.clientX - dragStartPos.current.x;
                const dy = e.clientY - dragStartPos.current.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance > 5) {
                    isDraggingRef.current = true;
                }
            }

            if (!isDraggingRef.current) return;

            const rect = imgRef.current.getBoundingClientRect();
            const xPct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const yPct = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

            setDragPosition({ xPct, yPct });
        },
        [draggingPin, imgRef]
    );

    const handleTouchMove = useCallback(
        (e) => {
            if (!draggingPin || !imgRef?.current) return;

            // Prevent page scrolling while touching a pin
            e.preventDefault();

            const touch = e.touches[0];
            if (!touch) return;

            // Only start a drag after movement exceeds threshold (avoids accidental drags on tap)
            if (!isDraggingRef.current && canDragRef.current && dragStartPos.current) {
                const dx = touch.clientX - dragStartPos.current.x;
                const dy = touch.clientY - dragStartPos.current.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance > 5) {
                    isDraggingRef.current = true;
                }
            }

            if (!isDraggingRef.current) return;

            const rect = imgRef.current.getBoundingClientRect();
            const xPct = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
            const yPct = Math.max(0, Math.min(1, (touch.clientY - rect.top) / rect.height));

            setDragPosition({ xPct, yPct });
        },
        [draggingPin, imgRef]
    );

    const handleDragEnd = useCallback(() => {
        if (!draggingPin) return;

        // If this was just a click/tap (not a drag), handle it as a pin click
        if (!isDraggingRef.current) {
            onPinClick(draggingPin);
        } else if (dragPosition && onPinDragEnd) {
            // This was a drag - update the pin position
            onPinDragEnd(draggingPin, dragPosition);
        }

        // Reset drag state
        setDraggingPin(null);
        setDragPosition(null);
        isDraggingRef.current = false;
        canDragRef.current = false;
        dragStartPos.current = null;
    }, [draggingPin, dragPosition, onPinClick, onPinDragEnd]);

    // Add non-passive touch event listeners to each pin to prevent pan/zoom interference
    // React synthetic events are passive by default, so we must use native listeners
    useEffect(() => {
        const currentPinRefs = pinRefs.current;
        const handlers = new Map();

        Object.entries(currentPinRefs).forEach(([pinId, element]) => {
            if (!element) return;

            const pin = pins.find((p) => p.deficiencyId === pinId);
            if (!pin) return;

            const touchStart = (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleTouchStart(e, pin);
            };

            handlers.set(element, touchStart);
            element.addEventListener('touchstart', touchStart, { passive: false });
        });

        return () => {
            handlers.forEach((touchStart, element) => {
                element.removeEventListener('touchstart', touchStart);
            });
        };
    }, [pins, handleTouchStart]);

    // Add global event listeners when dragging to track movement across entire document
    useEffect(() => {
        if (!draggingPin) return;

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleDragEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleDragEnd);
            document.body.style.cursor = 'default';
        };
    }, [draggingPin, handleMouseMove, handleTouchMove, handleDragEnd]);

    return (
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {pins.map((pin) => {
                const isHighlighted = highlightedPinId === pin.deficiencyId;
                const isDragging = draggingPin?.deficiencyId === pin.deficiencyId;
                const currentPosition =
                    isDragging && dragPosition ? dragPosition : { xPct: pin.xPct, yPct: pin.yPct };
                const isDraggable = !canDragPin || canDragPin(pin);

                return (
                    <Box
                        key={pin.id}
                        sx={{
                            position: 'absolute',
                            left: `${currentPosition.xPct * 100}%`,
                            top: `${currentPosition.yPct * 100}%`,
                            pointerEvents: 'none',
                            transition: isDragging ? 'none' : 'left 0.2s ease, top 0.2s ease',
                            zIndex: isDragging ? 1000 : 1,
                        }}
                    >
                        <Box
                            sx={{
                                transform: 'translate(-50%, -100%)',
                                transformOrigin: 'bottom center',
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    transform: `scale(${inversePinScale})`,
                                    transformOrigin: 'bottom center',
                                }}
                            >
                                <Box
                                    ref={(el) => {
                                        if (el) {
                                            pinRefs.current[pin.deficiencyId] = el;
                                        } else {
                                            delete pinRefs.current[pin.deficiencyId];
                                        }
                                    }}
                                    onMouseDown={(e) => handleMouseDown(e, pin)}
                                    onClick={(e) => {
                                        // Prevent direct pin clicks from creating a new deficiency.
                                        e.stopPropagation();
                                    }}
                                    sx={{
                                        display: 'flex',
                                        pointerEvents: 'auto',
                                        cursor: isDragging
                                            ? 'grabbing'
                                            : mobileMoveMode && isDraggable
                                              ? 'grab'
                                              : 'pointer',
                                        touchAction: 'none', // Disable default touch behaviors
                                        animation: isHighlighted
                                            ? 'wiggleScale 1.2s ease-in-out infinite'
                                            : 'none',
                                        opacity: isDragging ? 0.7 : 1,
                                        filter: isDragging
                                            ? 'drop-shadow(0 0 3px rgba(255,255,255,0.98)) drop-shadow(0 4px 8px rgba(0,0,0,0.38))'
                                            : 'drop-shadow(0 0 3px rgba(255,255,255,0.96)) drop-shadow(0 2px 4px rgba(0,0,0,0.32))',
                                        '@keyframes wiggleScale': {
                                            '0%': {
                                                transform: 'scale(1) rotate(0deg)',
                                            },
                                            '20%': {
                                                transform: 'scale(1.3) rotate(-5deg)',
                                            },
                                            '40%': {
                                                transform: 'scale(1.2) rotate(5deg)',
                                            },
                                            '60%': {
                                                transform: 'scale(1.3) rotate(-3deg)',
                                            },
                                            '80%': {
                                                transform: 'scale(1.1) rotate(2deg)',
                                            },
                                            '100%': {
                                                transform: 'scale(1) rotate(0deg)',
                                            },
                                        },
                                    }}
                                >
                                    <RoomIcon
                                        fontSize="large"
                                        sx={{ color: getPinColor(pin.pinColor) }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

PinsOverlay.propTypes = {
    pins: PropTypes.array.isRequired,
    scale: PropTypes.number,
    fitScale: PropTypes.number,
    onPinClick: PropTypes.func.isRequired,
    highlightedPinId: PropTypes.string,
    canDragPin: PropTypes.func,
    onPinDragEnd: PropTypes.func,
    imgRef: PropTypes.object,
    mobileMoveMode: PropTypes.bool,
};

export default function FloorPlanViewerWrapper({ isSidebarOpen }) {
    // Get projectId and floorplanId from URL params
    const { projectId, floorplanId } = useParams();

    return (
        <DeficiencyProvider projectId={projectId} floorplanId={floorplanId}>
            <FloorPlanViewer isSidebarOpen={isSidebarOpen} />
        </DeficiencyProvider>
    );
}

FloorPlanViewerWrapper.propTypes = {
    isSidebarOpen: PropTypes.bool.isRequired,
};
